package org.example.project.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import org.example.project.enginechat.loadStandaloneUnreadCount
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.enginekalender.loadOwnKalenderAufgaben
import org.example.project.screens_cus_mobile.loadMobileErlaubteStandorte
import org.example.project.screens_cus_mobile.mobileCurrentBearbeiterId
import org.example.project.screens_cus_mobile.mobileIsSelectableAngekommen
import org.example.project.screens_cus_mobile.mobileStandortErlaubt
import org.example.project.service.GlobalManager
import org.example.project.service.YardApi
import org.example.project.service.YardLiveSync
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date

internal const val CHAT_SCREEN_KEY = "ChatModuleScreen"
private const val TASK_SCREEN_KEY = "AufgabenViewer"

@Composable
internal fun rememberDashboardLiveBadges(screens: List<All>, includeMobileYard: Boolean): Map<String, Int> {
    val screenKeys = screens.map { it.key }.toSet()
    val yardVersion = YardLiveSync.version()
    val chatVersion = GlobalManager.jsonTopicVersion("room.json")
    val taskVersion = GlobalManager.jsonDomainVersion("aufgaben")
    var badges by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }
    var ownTasks by remember { mutableStateOf<List<All>>(emptyList()) }
    var clockTick by remember { mutableStateOf(js("Date.now()") as Double) }

    LaunchedEffect(screenKeys, includeMobileYard, yardVersion, chatVersion) {
        val next = mutableMapOf<String, Int>()
        if (CHAT_SCREEN_KEY in screenKeys) next[CHAT_SCREEN_KEY] = loadStandaloneUnreadCount()
        if (includeMobileYard) next.putAll(loadMobileYardBadgeCounts(screenKeys))
        badges = next
    }
    LaunchedEffect(screenKeys, taskVersion) {
        if (TASK_SCREEN_KEY !in screenKeys) {
            ownTasks = emptyList()
            return@LaunchedEffect
        }
        while (true) {
            runCatching { loadOwnKalenderAufgaben() }
                .onSuccess { ownTasks = it }
            delay(30_000)
        }
    }
    LaunchedEffect(screenKeys) {
        if (TASK_SCREEN_KEY !in screenKeys) return@LaunchedEffect
        while (true) {
            clockTick = js("Date.now()") as Double
            delay(1_000)
        }
    }
    val today = kalenderTodayIsoDate()
    val now = Date(clockTick)
    val nowSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
    val openTasksToday = ownTasks.count { task ->
        if (task.datum != today || task.aufgabeErledigt) return@count false
        val end = task.uhrzeitBis.split(":").mapNotNull { it.toIntOrNull() }
        val start = task.uhrzeitVon.split(":").mapNotNull { it.toIntOrNull() }
        val remainingUntil = if (end.size >= 2) end[0] * 3600 + end[1] * 60 else if (start.size >= 2) start[0] * 3600 + start[1] * 60 else -1
        remainingUntil > nowSeconds
    }
    return if (TASK_SCREEN_KEY in screenKeys) badges + (TASK_SCREEN_KEY to openTasksToday) else badges
}

private suspend fun loadMobileYardBadgeCounts(screenKeys: Set<String>): Map<String, Int> {
    val result = mutableMapOf<String, Int>()
    val allowedLocations = loadMobileErlaubteStandorte()
    val workerId = mobileCurrentBearbeiterId()

    if ("MobileWEAuswahlScreen" in screenKeys) {
        result["MobileWEAuswahlScreen"] = YardApi.loadWeLkwListe()
            .filter { it.zustand != ZustandManager.ABGESCHLOSSEN && it.zustand != ZustandManager.STORNIERT }
            .filter { mobileStandortErlaubt(it, allowedLocations) }
            .count { mobileIsSelectableAngekommen(it, workerId) }
    }
    if ("MobileWAAuswahlScreen" in screenKeys) {
        result["MobileWAAuswahlScreen"] = YardApi.loadWaLkwListe()
            .filter { it.zustand != ZustandManager.ABGESCHLOSSEN && it.zustand != ZustandManager.STORNIERT }
            .filter { mobileStandortErlaubt(it, allowedLocations) }
            .filter { mobileIsSelectableAngekommen(it, workerId) }
            .groupBy(::mobileWaBadgeGroupKey)
            .size
    }
    if ("MobileWAKontrolleScreen" in screenKeys) {
        val locations = YardApi.loadWaListe().flatMap { head ->
            YardApi.loadWaStandorteByTour(head.lieferscheinnummer)
                .filter { it.zustand != ZustandManager.STORNIERT && it.zustand != ZustandManager.ABGESCHLOSSEN }
                .filter { mobileStandortErlaubt(it, allowedLocations) }
        }
        val controlledKeys = locations.filter { it.kontrolliert }.map(::mobileWaControlKey).filter { it.isNotBlank() }.toSet()
        result["MobileWAKontrolleScreen"] = locations.count {
            val key = mobileWaControlKey(it)
            !it.kontrolliert && (key.isBlank() || key !in controlledKeys)
        }
    }
    return result
}

private fun mobileWaBadgeGroupKey(entry: All): String {
    val stored = entry.waSammelPin.ifBlank { entry.waSammelId }.ifBlank { entry.pin }.trim()
    return if (stored.isNotBlank()) "SAMMEL:$stored"
    else "SINGLE:${entry.firmenId}:${entry.lieferscheinnummer}:${entry.id.ifBlank { entry.lfdNr.toString() }}"
}

private fun mobileWaControlKey(entry: All): String =
    listOf(entry.platzcode, entry.platz, entry.standort, entry.slotStandortId, entry.waStandortId, entry.id)
        .firstOrNull { it.isNotBlank() }?.trim()?.lowercase().orEmpty()

@Composable
internal fun DashboardCountBadge(count: Int) {
    if (count <= 0) return
    Span({
        style {
            position(Position.Absolute)
            top((-5).px)
            right((-5).px)
            minWidth(20.px)
            height(20.px)
            padding(0.px, 5.px)
            borderRadius(999.px)
            backgroundColor(rgb(211, 37, 57))
            color(Color.white)
            fontSize(12.px)
            fontWeight("700")
            lineHeight(20.px)
            textAlign("center")
            boxSizing("border-box")
            property("z-index", "3")
            property("border", "1px solid rgba(255,255,255,0.14)")
        }
    }) { Text(if (count > 99) "99+" else count.toString()) }
}

@Composable
internal fun DashboardCardBadge(count: Int) {
    if (count <= 0) return
    Span({
        style {
            color(Color("#D32539"))
            fontSize(13.px)
            fontWeight("700")
            lineHeight("1")
            textAlign("center")
            property("font-variant-numeric", "tabular-nums")
        }
    }) { Text(if (count > 99) "99+" else count.toString()) }
}
